import { authFetch } from '@/utils/auth-fetch';

export interface ImpactProduct {
  item_id: string;
  first_seen: string | null;
  wave: string | null;
  seo_visits: number;
  llm_visits: number;
  seo_revenue: number;
  llm_revenue: number;
  seo_order_lines: number;
  llm_order_lines: number;
  productName: string | null;
  productUrl: string | null;
}

export interface ImpactProductListResponse {
  products: ImpactProduct[];
  total: number;
  dataUntil: string | null;
}

export interface ImpactSeriesPoint {
  day: string;
  seoVisits: number;
  llmVisits: number;
  isLastDay: boolean;
}

export interface ImpactDailyRow {
  day: string;
  seoVisits: number;
  llmVisits: number;
  seoRevenue: number;
  llmRevenue: number;
  seoOrderLines: number;
  llmOrderLines: number;
  botRequests: number;
  isLastDay: boolean;
}

export interface ImpactProductDetail {
  product: {
    itemId: string;
    domain: string;
    firstSeen: string | null;
    wave: string | null;
    productName: string | null;
    productUrl: string | null;
  };
  series: ImpactSeriesPoint[];
  daily: ImpactDailyRow[];
  dataUntil: string | null;
}

export type ImpactSortColumn = 'llm_visits' | 'seo_visits' | 'llm_revenue' | 'seo_revenue';

export interface ImpactSearchParams {
  q?: string;
  limit?: number;
  offset?: number;
  sort?: ImpactSortColumn;
  from?: string;
  to?: string;
}

export interface ImpactDetailParams {
  from?: string;
  to?: string;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const impactProductsService = {
  async list(siteId: string, params: ImpactSearchParams = {}): Promise<ImpactProductListResponse> {
    const qs = buildQuery(params as Record<string, string | number | undefined>);
    const res = await authFetch(`/api/impact-products/${siteId}${qs}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error || `HTTP ${res.status}`);
    }
    return res.json();
  },

  async detail(siteId: string, itemId: string, params: ImpactDetailParams = {}): Promise<ImpactProductDetail> {
    const qs = buildQuery(params as Record<string, string | undefined>);
    const res = await authFetch(`/api/impact-products/${siteId}/${encodeURIComponent(itemId)}${qs}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error || `HTTP ${res.status}`);
    }
    return res.json();
  },

  csvUrl(siteId: string, itemId: string, params: ImpactDetailParams = {}): string {
    const qs = buildQuery(params as Record<string, string | undefined>);
    return `/api/impact-products/${siteId}/${encodeURIComponent(itemId)}.csv${qs}`;
  },
};
